import * as Icons from '@livingdesign/icons';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { Button, Menu, MenuItem } from '@walmart-web/livingdesign-components';
import Cookies from 'js-cookie';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import analytics from '../../../adobe-analytics/analytics';
import { ANALYTICS } from '../../../adobe-analytics/analytics-data-elements';
import clicksTracker from '../../../adobe-analytics/clicksTracker';
import CloseModalLogo from '../../../assets/sidebar-cross-logo.svg';
import config from '../../../config/config';
import {
  ACNT_ADMIN_ROLE,
  ACNT_FINANCE_ROLE,
  ACNT_REVIEWER_ROLE,
  ACNT_SUPPORT_TIER_1,
  ACNT_SUPPORT_TIER_2,
  FinanceReports,
} from '../../../constants';
import { changeLanguage, SUPPORTED_LANGUAGES } from '../../../i18n';
import ACNTService from '../../../service';
import { fetchCCM } from '../../../utils';
import { getVerifiedRole, homePageRedirection } from '../../../utils/index';
import './topbar.scss';

export const clearCookies = () => {
  // Issue 70be9c41: CWE-614 — Include Secure + SameSite when expiring cookies
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const spcook = cookie.split('=');
    document.cookie =
      spcook[0].trim() + '=;Expires=Thu, 01 Jan 1970 00:00:01 GMT;Path=/;Secure;SameSite=Strict';
  }
};

export const handleLogout = () => {
  analytics.trackEventInfo({
    type: clicksTracker.EVENTS.CLICK,
    actionOn: clicksTracker.ACTION_ON.ACNT_APPLICATION_LOGOUT,
    location: ANALYTICS.Applicant_LandingPage.name,
  });
  clearCookies();
  window.location.href = config.logoutUrl;
};

export const SideBarCloseButtonGroup: React.FunctionComponent<any> = ({ closeSideBar }) => {
  const { t } = useTranslation();
  return (
    <button
      aria-label={t('a11y.closeDialog')}
      className="close-dialog-btn"
      type="button"
      data-testid="close-dia"
      onClick={closeSideBar}
    >
      <img alt="logo" src={CloseModalLogo} />
    </button>
  );
};

const TopBar = () => {
  const role = Cookies.get('userRole');
  const url = window.location.href;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [sidebarActive, setSidebarActive] = React.useState(false);
  
  const { data: ccmData } = useQuery(['GetFeatureFlags'], fetchCCM);
  
  const featureFlagsVisibility = ccmData?.featureFlags;
  const enabledLanguages = ccmData?.enabledLanguages;

  const handleRootNavigation = () => {
    homePageRedirection(role, navigate, url);
    closeSideBar();
  };

  const toggleSidebar = () => {
    setSidebarActive(true);
  };
  const closeSideBar = () => {
    setSidebarActive(false);
  };

  return (
    <>
      {sidebarActive && (
        <div data-focus-lock-disabled="false" className="sidebar-navigation">
          <div className="sidebar-navigation-spread">
            <div aria-labelledby="react-aria7811842470-15" className="sidebar-wrapper" role="dialog">
              <div className="nav-header-wrap">
                <SideBarCloseButtonGroup closeSideBar={closeSideBar} />
                <h2 className="nav-header" id="react-aria7811842470-15">
                  {t('common.menu')}
                </h2>
              </div>
              <div className="nav-content-wrapper">
                <div className="navbar-content">
                  <div className="ho-side-bar-content">
                    <div>
                      <div className="navlink-text" data-testid="navlink-home" onClick={handleRootNavigation}>
                        <strong>{t('common.home')}</strong>
                      </div>
                      {getVerifiedRole(
                        [
                          ACNT_FINANCE_ROLE,
                          ACNT_ADMIN_ROLE,
                          ACNT_REVIEWER_ROLE,
                          ACNT_SUPPORT_TIER_1,
                          ACNT_SUPPORT_TIER_2,
                        ],
                        url
                      ) && <ReviewerDashboard onCloseMenu={closeSideBar} />}
                      {getVerifiedRole([ACNT_ADMIN_ROLE], url) && <AdminMenu onCloseMenu={closeSideBar} />}
                      {getVerifiedRole([ACNT_FINANCE_ROLE], url) && <FinanceDashboard onCloseMenu={closeSideBar} />}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="backdrop" onClick={() => closeSideBar()} data-testid="close-bar"></div>
        </div>
      )}
      <div className={`main ${sidebarActive ? 'active' : ''}`}>
        <nav>
          <AppBar position="fixed" component="nav" className="app-bar">
            <Toolbar>
              <div className="hamburger" onClick={toggleSidebar} data-testid="hamburger-click">
                <IconButton size="large" edge="start" color="primary" aria-label="menu" sx={{ mr: 2 }}>
                  <Icons.Menu size="medium" color="white" />
                </IconButton>
              </div>
              <div className="app-bar-title">
                <AppName />
              </div>
              {featureFlagsVisibility?.MULTI_LANGUAGE_SUPPORT && (
                <LanguageSwitcher enabledLanguages={enabledLanguages} />
              )}
              <UserMenu />
            </Toolbar>
          </AppBar>
        </nav>
      </div>
    </>
  );
};

export const LanguageSwitcherSelect = ({
  enabledLanguages,
  onClose,
}: {
  enabledLanguages?: string[];
  onClose?: () => void;
}) => {
  const { i18n, t } = useTranslation();

  const availableLanguages = enabledLanguages
    ? SUPPORTED_LANGUAGES.filter(lang => enabledLanguages.includes(lang.code))
    : SUPPORTED_LANGUAGES.filter(lang => lang.code === 'en');

  return (
    <select
      value={i18n.language}
      onChange={(e) => {
        changeLanguage(e.target.value);
        ACNTService.saveLanguageSelection(e.target.value);
        onClose?.();
      }}
      aria-label={t('language.select')}
      data-testid="language-switcher"
      className="language-switcher"
    >
      {availableLanguages.map(({ code, label }) => (
        <option key={code} value={code} style={{ color: '#000' }}>
          {label}
        </option>
      ))}
    </select>
  );
};

export const LanguageSwitcher = ({ enabledLanguages }: { enabledLanguages?: string[] }) => {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { data: userDetails } = useQuery('getUserDetails', ACNTService.getUserDetails);
  const preferenceApplied = React.useRef(false);

  React.useEffect(() => {
    if (userDetails?.preferredLocale && !preferenceApplied.current) {
      preferenceApplied.current = true;
      changeLanguage(userDetails.preferredLocale);
    }
  }, [userDetails?.preferredLocale]);

  React.useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="language-switcher-dropdown" ref={containerRef}>
      <button
        type="button"
        className="language-switcher-label"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((prev) => !prev)}
      >
        {t('nav.language')}
      </button>
      {open && (
        <div className="language-switcher-panel">
          <p className="language-switcher-disclaimer">
            {t('nav.languageNotice')}
          </p>
          <div className="language-switcher-row">
            <span className="language-switcher-panel-label">{t('nav.languageSelect')}: </span>
            <LanguageSwitcherSelect enabledLanguages={enabledLanguages} onClose={() => setOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

const AppName = () => {
  const role = Cookies.get('userRole');
  const url = window.location.href;
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <Typography
      className="app-bar-header"
      variant="h6"
      style={{ cursor: 'pointer' }}
      sx={{ flexGrow: 1 }}
      data-testid="nav-bar-header"
      onClick={() => homePageRedirection(role, navigate, url)}
    >
      {t('nav.appTitle')}
    </Typography>
  );
};

const UserMenu = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const ref = React.useRef<any>();
  const { t } = useTranslation();

  return (
    <div className="group-contact">
      <Menu
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onOpen={() => setIsOpen(true)}
        trigger={
          <Button
            variant="tertiary"
            ref={ref}
            UNSAFE_style={{ color: '#FFF' }}
            data-testid="user-menu-btn"
            UNSAFE_className="group-contact-btn"
          >
            <Icons.User size="medium" color="white" />
          </Button>
        }
        triggerRef={ref}
      >
        <MenuItem onClick={handleLogout}>{t('common.logout')}</MenuItem>
      </Menu>
    </div>
  );
};

const ReviewerDashboard: React.FC<{ onCloseMenu: () => void }> = ({ onCloseMenu }) => {
  const { t } = useTranslation();
  return (
    <div className="navlink-text" data-testid="navlink-reviewer">
      <Link className="navlink-reviewer-text" to="/reviewer/dashboard" onClick={onCloseMenu}>
        <strong>{t('nav.reviewerDashboard')}</strong>
      </Link>
    </div>
  );
};

const FinanceDashboard: React.FC<{ onCloseMenu: () => void }> = ({ onCloseMenu }) => {
  const { t } = useTranslation();
  return (
    <div className="navlink-text" data-testid="navlink-finance">
      <Link className="navlink-reviewer-text" to="/finance-report" onClick={onCloseMenu}>
        <strong>{t('nav.financeReports')}</strong>
      </Link>
    </div>
  );
};

const AdminMenu: React.FC<{ onCloseMenu: () => void }> = ({ onCloseMenu }) => {
  const [expandAdminMenu, setExpandAdminMenu] = React.useState(false);
  const { t } = useTranslation();

  return (
    <>
      <div className="navlink-text" data-testid="navlink-admin" onClick={() => setExpandAdminMenu((prev) => !prev)}>
        <strong>{t('nav.admin')}</strong>
        {expandAdminMenu ? <Icons.ChevronDown /> : <Icons.ChevronRight />}
      </div>
      {expandAdminMenu && (
        <div className="admin-submenu">
          <Link className="navlink-text navlink-submenu" to="/admin/configure-hardship" onClick={onCloseMenu}>
            <strong>{t('nav.adminConfigureHardship')}</strong>
          </Link>
          <Link className="navlink-text navlink-submenu" to="/admin/configure-grant-request" onClick={onCloseMenu}>
            <strong>{t('nav.adminConfigureGrantRequest')}</strong>
          </Link>
          <Link className="navlink-text navlink-submenu" to="/admin/configure-disaster" onClick={onCloseMenu}>
            <strong>{t('nav.adminConfigureDisaster')}</strong>
          </Link>
          <Link className="navlink-text navlink-submenu" to="/admin/configure-faq" onClick={onCloseMenu}>
            <strong>{t('nav.adminConfigureFaq')}</strong>
          </Link>
          <Link
            className="navlink-text navlink-submenu"
            to={`/admin/finance-report#${FinanceReports.DetailedReport}`}
            onClick={onCloseMenu}
          >
            <strong>{t('nav.adminGenerateDetailedReport')}</strong>
          </Link>
          <Link className="navlink-text navlink-submenu" to="/admin/finance-report" onClick={onCloseMenu}>
            <strong>{t('nav.adminGenerateFinanceReport')}</strong>
          </Link>
        </div>
      )}
    </>
  );
};

export default TopBar;
